import { useRef, useState } from "react";

import { Button } from "~/components";
import { downloadBlob } from "~/shared/utils/download-blob";
import { DropzoneContainer, EmptyCsvState, HiddenFileInput, icons } from "~/ui";
import { validateError } from "~/utils";
import { useImportGlobalScheduleRules } from "./use-import-global-schedule-rules";

const TEMPLATE_HEADER =
  "TYPE,CALENDAR,START_DATE,END_DATE,START_TIME,END_TIME,TIMEZONE,RRULE,EXCEPTION,CLINICIAN_EXCEPTION";

const downloadTemplate = () =>
  downloadBlob(
    new Blob([`${TEMPLATE_HEADER}\n`], { type: "text/csv" }),
    "global-block-rules-template.csv",
  );

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface GlobalRulesUploadScreenProps {
  onSuccess: (imported: number) => void;
  onCancel: () => void;
}

export const GlobalRulesUploadScreen = ({
  onSuccess,
  onCancel,
}: GlobalRulesUploadScreenProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [clientError, setClientError] = useState<string | undefined>();
  const [rowErrors, setRowErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadState = selectedFile
    ? "ready"
    : rowErrors.length > 0
      ? "error"
      : "empty";

  const importMutation = useImportGlobalScheduleRules({
    onSuccess: (imported) => {
      clearSelection();
      onSuccess(imported);
    },
    onError: (error) => {
      const parsed = validateError(error);
      if (
        parsed &&
        parsed.response.data.error.code === "validation_failed" &&
        parsed.response.data.error.fields
      ) {
        const fileErrors = parsed.response.data.error.fields["file"] ?? [];
        setRowErrors(fileErrors);
      } else {
        setRowErrors([
          parsed?.response.data.error.message ?? "Something went wrong.",
        ]);
      }
      clearFileInput();
      setSelectedFile(null);
    },
  });

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setClientError("Only CSV files are allowed.");
      return;
    }

    setClientError(undefined);
    setRowErrors([]);
    setSelectedFile(file);
  };

  // Resetting React state is not enough: re-picking the same file fires no change event
  // unless the native input's value is cleared too.
  const clearFileInput = () => {
    if (inputRef.current) inputRef.current.value = "";
  };

  const clearSelection = () => {
    clearFileInput();
    setSelectedFile(null);
    setClientError(undefined);
    setRowErrors([]);
  };

  const handleRemove = () => {
    clearSelection();
  };

  const handleSave = () => {
    if (!selectedFile) return;
    importMutation.mutate(selectedFile);
  };

  return (
    <div className="flex h-full flex-col justify-between">
      <div className="overflow-y-auto">
        {uploadState === "empty" && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={downloadTemplate}
              className="text-violet mb-5 gap-2 px-0 hover:underline"
            >
              <icons.outline.ArrowDownTrayIcon className="size-4" />
              Download CSV template
            </Button>

            <DropzoneContainer
              className="cursor-pointer"
              onDropFiles={handleFiles}
              onClick={() => inputRef.current?.click()}
            >
              <div className="relative flex min-h-36 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50 px-6 py-8 text-center transition hover:border-violet-500 hover:bg-violet-100">
                <HiddenFileInput ref={inputRef} onChange={handleFiles} />
                <EmptyCsvState />
                <p className="text-xs text-neutral-500">
                  Supported formats: CSV · Max 2 MB
                </p>
              </div>
            </DropzoneContainer>

            {clientError && (
              <p className="mt-2 text-sm text-red-600">{clientError}</p>
            )}
          </>
        )}

        {selectedFile && (
          <>
            <div className="mb-4 flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <icons.solid.CheckCircleIcon className="size-5 shrink-0 text-green-500" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-neutral-800">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-neutral-500">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                className="px-0 text-red-500 hover:underline"
              >
                Remove
              </Button>
            </div>

            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <icons.outline.ExclamationCircleIcon className="mt-0.5 size-4 shrink-0 text-amber-600" />
              <p className="text-sm leading-snug text-amber-800">
                This will{" "}
                <strong>replace all existing global block rules</strong> with
                the rows in this file. This action cannot be undone. Make sure
                your CSV includes all rules you want active.
              </p>
            </div>
          </>
        )}

        {uploadState === "error" && (
          <>
            <div className="mb-4 flex items-center gap-3 rounded-lg border border-red-300 bg-red-50 p-3">
              <icons.solid.XCircleIcon className="size-5 shrink-0 text-red-500" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-neutral-800">
                  Upload failed
                </p>
                <p className="text-xs text-red-600">
                  {rowErrors.length} error
                  {rowErrors.length !== 1 ? "s" : ""} found — no rules were
                  saved
                </p>
              </div>
            </div>

            <div className="mb-5 rounded-lg border border-red-300 bg-red-50 p-4">
              <p className="mb-2 text-sm font-semibold text-red-700">
                Fix these errors and re-upload:
              </p>
              <ul className="list-inside list-disc space-y-1">
                {rowErrors.map((msg, i) => (
                  <li key={i} className="text-sm text-red-800">
                    {msg}
                  </li>
                ))}
              </ul>
            </div>

            <DropzoneContainer
              className="cursor-pointer"
              onDropFiles={handleFiles}
              onClick={() => inputRef.current?.click()}
            >
              <div className="relative flex min-h-28 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50 px-6 py-6 text-center transition hover:border-violet-500 hover:bg-violet-100">
                <HiddenFileInput ref={inputRef} onChange={handleFiles} />
                <EmptyCsvState
                  label={
                    <>
                      Drop a corrected file or{" "}
                      <span className="text-violet font-medium">
                        click to browse
                      </span>
                    </>
                  }
                />
              </div>
            </DropzoneContainer>

            {clientError && (
              <p className="mt-2 text-sm text-red-600">{clientError}</p>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={uploadState !== "ready" || importMutation.isPending}
        >
          {importMutation.isPending ? "Saving..." : "Save rules"}
        </Button>
      </div>
    </div>
  );
};
